import z from 'zod'
import { safeParseJson } from '@fusion-advantage/backcompat'

// on web, SAML login is a full-page redirect and the API always sends the
// browser back to the site root; to support deep links (e.g. an encounter or
// a branch preview) we remember the path in this tab's sessionStorage before
// leaving, and restore it when the browser comes back to the root

const SESSION_STORAGE_KEY = 'LoginReturnPath'
// generous enough for MFA, short enough that an abandoned login
// doesn't hijack a later visit to the root in the same tab
const MAX_AGE_MS = 15 * 60 * 1000

const ZStoredLoginReturnPath = z.object({
	path: z.string(),
	savedAt: z.number(),
})

// same-origin path only; reject protocol-relative ('//host', '/\host')
const isSafeReturnPath = (path: string): boolean => {
	return path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\')
}

export const saveLoginReturnPath = (): void => {
	const path = window.location.pathname + window.location.search
	try {
		if(path === '/' || !isSafeReturnPath(path)) {
			sessionStorage.removeItem(SESSION_STORAGE_KEY)
			return
		}
		sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
			path,
			savedAt: Date.now(),
		}))
	} catch {
		// storage unavailable; login still works, it just lands on the root
	}
}

// must run before the router is created, so it reads the restored location
export const restoreLoginReturnPath = (): void => {
	let value: string|null
	try {
		value = sessionStorage.getItem(SESSION_STORAGE_KEY)
		sessionStorage.removeItem(SESSION_STORAGE_KEY)
	} catch {
		return
	}
	if(!value || window.location.pathname !== '/') {
		return
	}
	const rParse = safeParseJson(value, ZStoredLoginReturnPath)
	if(rParse.isErr()) {
		return
	}
	const { path, savedAt } = rParse.value
	if(Date.now() - savedAt > MAX_AGE_MS || !isSafeReturnPath(path)) {
		return
	}
	window.history.replaceState(null, '', path)
}
